// What one step of a lesson looks like on a learner's screen.
//
// Rendered from the payload the author is editing, so a mistake shows up
// here rather than on a phone twenty minutes later. The pitches use the
// app's real geometry (see geometry.ts); the surrounding chrome is a
// faithful but not pixel-identical rebuild — the point is to see the
// content, not to replace device testing.
//
// Answer keys are shown as REVEALED throughout. An author is checking
// that the right answer is the right one, which is exactly what a learner
// must not see up front and what the author most needs to.
import {
  FormationBoard,
  MarkingsPitch,
  ZonePitch,
  roleLabel,
} from "./Pitches";
import { PITCH_COLOR as C, zoneLabel } from "./geometry";
import ScenePreview from "./ScenePreview";

type Payload = Record<string, any>;

const PITCH_W = (deviceWidth: number) => Math.min(deviceWidth - 40, 330);

export default function AssetPreview({
  kind,
  payload,
  width,
}: {
  kind: string;
  payload: Payload;
  width: number;
}) {
  const p = payload ?? {};
  const w = PITCH_W(width);

  switch (kind) {
    case "notice_prompt":
      return (
        <Stack>
          <Eyebrow>NOTICE</Eyebrow>
          <Lead>{p.primeText}</Lead>
          <Body>{p.noticeQuestion}</Body>
        </Stack>
      );

    case "explanation":
      return <ExplanationPreview payload={p} width={width} />;

    case "reason_prompt":
      return (
        <Stack>
          <Eyebrow>THINK LIKE A COACH</Eyebrow>
          <Lead>{p.question}</Lead>
          <Reveal>{p.revealedReasoning}</Reveal>
        </Stack>
      );

    case "match_mission":
      return (
        <Stack>
          <Eyebrow>MATCH MISSION</Eyebrow>
          <Lead>{p.task}</Lead>
          <Body>{p.durationHint}</Body>
        </Stack>
      );

    case "reflection_prompt":
      return (
        <Stack>
          <Eyebrow>REFLECT</Eyebrow>
          {(p.prompts ?? []).map((q: string, i: number) => (
            <Body key={i}>• {q}</Body>
          ))}
        </Stack>
      );

    case "diagram":
      return (
        <Stack>
          <Eyebrow>WATCH</Eyebrow>
          <Lead>{p.caption}</Lead>
          <ZonePitch
            width={w}
            highlight={p.zones ?? []}
            tone="gold"
            path={[]}
          />
          {(p.arrows ?? []).map((a: any, i: number) => (
            <Body key={i}>
              {zoneLabel(a.from)} → {zoneLabel(a.to)} · {a.label}
            </Body>
          ))}
        </Stack>
      );

    case "formation_board":
      return (
        <Stack>
          <Eyebrow>WATCH THE BOARD</Eyebrow>
          <Lead>{p.caption}</Lead>
          <FormationBoard
            width={w}
            formation={p.formation}
            team={p.team}
            highlight={p.highlight ?? []}
            moves={p.moves ?? []}
          />
          {(p.moves ?? []).map((m: any, i: number) => (
            <Body key={i}>
              {roleLabel(m.role)} {m.label ?? `moves to ${zoneLabel(m.to)}`}
            </Body>
          ))}
        </Stack>
      );

    case "pitch_markings":
      return (
        <Stack>
          <Eyebrow>WATCH THE LINES</Eyebrow>
          {(p.steps ?? []).map((s: any, i: number) => (
            <div key={i} className="space-y-2">
              <Lead>{s.caption}</Lead>
              <MarkingsPitch width={w} highlight={s.highlight ?? []} />
            </div>
          ))}
        </Stack>
      );

    case "play_scene":
      return (
        <Stack>
          <Eyebrow>WATCH THE PLAY</Eyebrow>
          <ScenePreview payload={p} width={w} />
        </Stack>
      );

    case "assessment_scene":
      return (
        <Stack>
          <Eyebrow>YOUR TURN · WATCH, THEN ANSWER</Eyebrow>
          <Lead>{p.text}</Lead>
          {/* Fixed to the last frame, exactly as the app does — a
              question scene asks about one moment, and letting the
              preview step back would show a picture the learner never
              sees. */}
          <ScenePreview payload={p} width={w} fixedToLastFrame />
          {p.mode === "choice" &&
            (p.options ?? []).map((o: any) => (
              <Option key={o.id} correct={o.id === p.correctOptionId}>
                {o.text}
              </Option>
            ))}
          {p.mode === "pick_player" && (
            <Answer>
              Answer: {(p.correctPlayerIds ?? []).join(", ")} — shown green
            </Answer>
          )}
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    case "assessment_mcq":
      return (
        <Stack>
          <Eyebrow>YOUR TURN</Eyebrow>
          <Lead>{p.text}</Lead>
          {(p.options ?? []).map((o: any) => (
            <Option key={o.id} correct={o.id === p.correctOptionId}>
              {o.text}
            </Option>
          ))}
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    case "assessment_multi_select":
      return (
        <Stack>
          <Eyebrow>YOUR TURN · PICK ALL THAT APPLY</Eyebrow>
          <Lead>{p.text}</Lead>
          {(p.options ?? []).map((o: any) => (
            <Option
              key={o.id}
              correct={(p.correctOptionIds ?? []).includes(o.id)}
            >
              {o.text}
            </Option>
          ))}
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    case "assessment_pitch_zone":
      return (
        <Stack>
          <Eyebrow>YOUR TURN · TAP THE PITCH</Eyebrow>
          <Lead>{p.text}</Lead>
          <ZonePitch width={w} highlight={p.correctZoneIds ?? []} />
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    case "assessment_pitch_marking":
      return (
        <Stack>
          <Eyebrow>YOUR TURN · TAP THE LINE</Eyebrow>
          <Lead>{p.text}</Lead>
          <MarkingsPitch width={w} highlight={p.correctMarkingIds ?? []} />
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    case "assessment_formation":
      return (
        <Stack>
          <Eyebrow>YOUR TURN · MOVE THE PLAYERS</Eyebrow>
          <Lead>{p.text}</Lead>
          <FormationBoard
            width={w}
            formation={p.formation}
            team={p.team}
            highlight={[
              ...(p.highlightRole ? [p.highlightRole] : []),
              ...(p.correctRoles ?? []),
            ]}
            moves={p.moves ?? []}
          />
          {p.mode === "spot" ? (
            <Answer>
              Answer: {(p.correctRoles ?? []).map(roleLabel).join(", ")}
            </Answer>
          ) : (
            <Answer>
              Answer: {(p.correctZoneIds ?? []).map(zoneLabel).join(" or ")}
            </Answer>
          )}
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    case "assessment_trace_pass":
      return (
        <Stack>
          <Eyebrow>YOUR TURN · TRACE THE PASS</Eyebrow>
          <Lead>{p.text}</Lead>
          <ZonePitch
            width={w}
            startZoneId={p.startZoneId}
            path={p.correctPath ?? []}
            tone="gold"
          />
          <Answer>
            {[
              ...(p.startZoneId ? [p.startZoneId] : []),
              ...(p.correctPath ?? []),
            ]
              .map(zoneLabel)
              .join("  →  ")}
          </Answer>
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    case "assessment_board_place":
      return (
        <Stack>
          <Eyebrow>YOUR TURN · PLACE THE PLAYERS</Eyebrow>
          <Lead>{p.text}</Lead>
          <div className="flex flex-wrap gap-1.5">
            {(p.rolesToPlace ?? []).map((r: string) => (
              <span
                key={r}
                className="rounded-lg border px-2 py-1 text-xs font-bold"
                style={{ borderColor: C.gold, color: C.gold }}
              >
                {roleLabel(r)}
              </span>
            ))}
          </div>
          <FormationBoard
            width={w}
            formation={p.formation}
            rolesToPlace={p.rolesToPlace ?? []}
            placements={p.correctZoneByRole ?? {}}
            highlight={p.rolesToPlace ?? []}
          />
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    case "assessment_match_pairs":
      return (
        <Stack>
          <Eyebrow>YOUR TURN · MATCH THEM UP</Eyebrow>
          <Lead>{p.text}</Lead>
          {(p.terms ?? []).map((t: any) => {
            const target = (p.targets ?? []).find(
              (g: any) => g.id === (p.correctPairs ?? {})[t.id]
            );
            return (
              <div
                key={t.id}
                className="rounded-lg border p-2 text-xs"
                style={{ borderColor: "rgba(255,255,255,0.14)" }}
              >
                <span style={{ color: C.ink, fontWeight: 700 }}>{t.text}</span>
                <span style={{ color: C.correct }}> → {target?.text ?? "—"}</span>
              </div>
            );
          })}
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    case "assessment_sort_buckets":
      return (
        <Stack>
          <Eyebrow>YOUR TURN · SORT THEM</Eyebrow>
          <Lead>{p.text}</Lead>
          {(p.buckets ?? []).map((b: any) => (
            <div
              key={b.id}
              className="rounded-lg border p-2"
              style={{ borderColor: "rgba(255,255,255,0.14)" }}
            >
              <div className="text-xs font-bold" style={{ color: C.gold }}>
                {b.text}
              </div>
              {(p.items ?? [])
                .filter((i: any) => (p.correctBucketByItem ?? {})[i.id] === b.id)
                .map((i: any) => (
                  <div
                    key={i.id}
                    className="mt-1 text-xs"
                    style={{ color: C.inkDim }}
                  >
                    • {i.text}
                  </div>
                ))}
            </div>
          ))}
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    case "assessment_order_steps":
      return (
        <Stack>
          <Eyebrow>YOUR TURN · PUT IT IN ORDER</Eyebrow>
          <Lead>{p.text}</Lead>
          {(p.correctOrder ?? []).map((id: string, i: number) => {
            const step = (p.steps ?? []).find((s: any) => s.id === id);
            return (
              <div
                key={id}
                className="flex items-center gap-2 rounded-lg border p-2"
                style={{ borderColor: "rgba(255,255,255,0.14)" }}
              >
                <span
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-extrabold"
                  style={{ background: C.gold, color: C.goldInk }}
                >
                  {i + 1}
                </span>
                <span className="text-xs" style={{ color: C.ink }}>
                  {step?.text ?? id}
                </span>
              </div>
            );
          })}
          <Reveal>{p.explanation}</Reveal>
        </Stack>
      );

    default:
      return (
        <Stack>
          <Eyebrow>{kind}</Eyebrow>
          <pre
            className="overflow-auto rounded p-2 text-[10px]"
            style={{ background: "rgba(0,0,0,0.3)", color: C.inkDim }}
          >
            {JSON.stringify(p, null, 2)}
          </pre>
        </Stack>
      );
  }
}

// ─── Beats ──────────────────────────────────────────────────────────────

function ExplanationPreview({
  payload,
  width,
}: {
  payload: Payload;
  width: number;
}) {
  // Mirrors buildExplanationBeats: authored beats win, otherwise the four
  // fields are split automatically.
  const beats =
    payload.beats?.length > 0
      ? payload.beats
      : [
          { title: "What it is", text: payload.whatIsIt },
          { title: "Why it matters", text: payload.whyItMatters },
          { title: "How to spot it", text: payload.howToRecognize },
          { title: "How to use it", text: payload.howToApply },
        ].filter((b) => !!b.text);

  const w = PITCH_W(width);

  return (
    <Stack>
      <Eyebrow>LEARN · {beats.length} beats</Eyebrow>
      {beats.map((b: any, i: number) => (
        <div
          key={i}
          className="space-y-2 rounded-lg border p-2.5"
          style={{ borderColor: "rgba(255,255,255,0.1)" }}
        >
          <div
            className="text-[10px] font-extrabold tracking-wider"
            style={{ color: C.gold }}
          >
            {b.title?.toUpperCase()} · {i + 1}/{beats.length}
          </div>
          <Body>{b.text}</Body>
          {b.visual && <BeatVisual visual={b.visual} width={w} />}
        </div>
      ))}
    </Stack>
  );
}

function BeatVisual({ visual, width }: { visual: any; width: number }) {
  switch (visual.kind) {
    case "compare":
      return (
        <div className="flex gap-2">
          {[visual.left, visual.right].map((side: any, i: number) => (
            <div key={i} className="flex-1 space-y-1">
              <div
                className="text-[10px] font-extrabold"
                style={{ color: C.gold }}
              >
                {side?.label}
              </div>
              <ZonePitch
                width={(width - 8) / 2}
                highlight={side?.zones ?? []}
                tone={side?.tone === "risk" ? "gold" : "correct"}
                labels={false}
              />
              <div className="text-[10px]" style={{ color: C.inkDim }}>
                {side?.bandLabel}
              </div>
              <div className="text-[10px]" style={{ color: C.inkDim }}>
                {side?.note}
              </div>
            </div>
          ))}
        </div>
      );

    case "sequence":
      return (
        <div className="space-y-1.5">
          {(visual.steps ?? []).map((s: any, i: number) => (
            <div key={i} className="flex gap-2">
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold"
                style={{ background: C.gold, color: C.goldInk }}
              >
                {i + 1}
              </span>
              <div>
                <div
                  className="text-[11px] font-bold"
                  style={{ color: C.ink }}
                >
                  {s.label}
                </div>
                <div className="text-[11px]" style={{ color: C.inkDim }}>
                  {s.text}
                </div>
              </div>
            </div>
          ))}
        </div>
      );

    case "stat":
      return (
        <div
          className="rounded-xl border p-3 text-center"
          style={{
            borderColor: "rgba(255,215,0,0.4)",
            background: "rgba(255,215,0,0.08)",
          }}
        >
          <div
            className="font-extrabold leading-none"
            style={{ fontSize: 34, color: C.gold }}
          >
            {visual.value}
          </div>
          <div className="mt-1 text-[11px] font-bold" style={{ color: C.ink }}>
            {visual.label}
          </div>
          {visual.caption && (
            <div className="mt-1 text-[10px]" style={{ color: C.inkDim }}>
              {visual.caption}
            </div>
          )}
        </div>
      );

    case "zones":
      return (
        <ZonePitch width={width} highlight={visual.zones ?? []} tone="gold" />
      );

    case "board":
      return (
        <FormationBoard
          width={width}
          formation={visual.formation}
          team={visual.team}
          highlight={visual.highlight ?? []}
          moves={visual.moves ?? []}
        />
      );

    case "markings":
      return <MarkingsPitch width={width} highlight={visual.highlight ?? []} />;

    default:
      return null;
  }
}

// ─── Small pieces of the lesson's chrome ────────────────────────────────

function Stack({ children }: { children: React.ReactNode }) {
  return <div className="space-y-2.5">{children}</div>;
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-[10px] font-extrabold tracking-wider"
      style={{ color: C.gold }}
    >
      {children}
    </div>
  );
}

function Lead({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-sm font-bold leading-snug"
      style={{ color: C.ink }}
    >
      {children}
    </p>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs leading-relaxed" style={{ color: C.inkDim }}>
      {children}
    </p>
  );
}

function Option({
  children,
  correct,
}: {
  children: React.ReactNode;
  correct: boolean;
}) {
  return (
    <div
      className="rounded-lg border p-2 text-xs"
      style={{
        borderColor: correct ? C.correct : "rgba(255,255,255,0.14)",
        background: correct ? "rgba(34,197,94,0.16)" : "transparent",
        color: correct ? C.correct : C.ink,
        fontWeight: correct ? 700 : 400,
      }}
    >
      {children}
    </div>
  );
}

function Answer({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-bold" style={{ color: C.correct }}>
      {children}
    </div>
  );
}

function Reveal({ children }: { children?: React.ReactNode }) {
  if (!children) return null;
  return (
    <div
      className="rounded-lg border-l-2 p-2 text-[11px] leading-relaxed"
      style={{
        borderColor: C.gold,
        background: "rgba(255,215,0,0.07)",
        color: C.inkDim,
      }}
    >
      {children}
    </div>
  );
}
